import * as React from "react"
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import { chartConfig } from "@/config/charts"
import { cn } from "@/lib/utils"
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

interface BarChartProps {
  data: any
  toggledKeys?: string[]
  className?: string
  xAxisKey?: string
  yAxisLabel?: string
}

export function BarChartMonthlyComponent({
  data,
  toggledKeys = [],
  className,
  xAxisKey = "month",
  yAxisLabel = "Value",
}: BarChartProps) {
  const [activeSeries, setActiveSeries] = React.useState<Array<string>>([])

  const handleLegendClick = (dataKey: string) => {
    setActiveSeries((prev) =>
      prev.includes(dataKey)
        ? prev.filter((el) => el !== dataKey)
        : [...prev, dataKey],
    )
  }

  React.useEffect(() => {
    setActiveSeries((prev) => {
      const newKeys = toggledKeys.filter((key) => !prev.includes(key))
      return newKeys.length > 0 ? [...prev, ...newKeys] : prev
    })
  }, [toggledKeys])

  const seriesKeys =
    data && data.length > 0
      ? Object.keys(data[0]).filter((key) => key !== xAxisKey)
      : []

  return (
    <div className={cn("w-full p-4", className)}>
      {!data || data.length === 0 ? (
        <div className="flex h-[345px] items-center justify-center">
          <span className="text-muted-foreground">No data available</span>
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="h-auto w-full">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <YAxis
                label={{
                  value: yAxisLabel,
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <XAxis
                dataKey={xAxisKey}
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value) => value}
              />
              <ChartTooltip
                content={<ChartTooltipContent hideLabel indicator="dot" />}
              />
              <ChartLegend
                onClick={(entry: any) => handleLegendClick(entry.dataKey)}
                content={<ChartLegendContent />}
              />
              {seriesKeys.map((key) => (
                <Bar
                  key={key}
                  hide={activeSeries.includes(key)}
                  dataKey={key}
                  fill={
                    chartConfig[key as keyof typeof chartConfig]?.color ||
                    "#8884d8"
                  }
                  radius={[8, 8, 0, 0]}
                  barSize={60} // Adjust the width of the bars
                >
                  {/* Display actual values on top of each bar */}
                  <LabelList
                    dataKey={key}
                    position="top"
                    fontSize={13}
                    fill="black"
                  />
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}
    </div>
  )
}
